import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft, FiX } from 'react-icons/fi';
import siteVisitService from '../services/siteVisitService';
import technicianService from '../services/technicianService';
import helperService from '../services/helperService';
import inventoryService from '../services/inventoryService';
import toolService from '../services/toolService';
import customerService from '../services/customerService';
import Input from '../components/Input';
import Button from '../components/Button';
import './SiteVisitForm.css';

const SiteVisitForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [helpers, setHelpers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [tools, setTools] = useState([]);

  const [formData, setFormData] = useState({
    complaint: '',
    serviceType: 'Repair',
    priority: 'medium',
    assignedTechnician: '',
    assignedHelper: '',
    scheduledDate: '',
    scheduledTime: '',
    notes: '',
  });

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [itemToAdd, setItemToAdd] = useState({ inventory: '', quantity: 1 });
  const [toolToAdd, setToolToAdd] = useState('');

  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isEdit) {
      fetchExistingVisit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      const [techResponse, helperResponse, invResponse, toolResponse] = await Promise.all([
        technicianService.getAvailable(),
        helperService.getAll(),
        inventoryService.getAll(),
        toolService.getAll({ status: 'available' }),
      ]);

      setTechnicians(techResponse.data);
      setHelpers(helperResponse.data);
      setInventoryItems(invResponse.data);
      setTools(toolResponse.data);

      // Fetch initial customers list for dropdown
      const customerResponse = await customerService.getAll({
        page: 1,
        limit: 50,
      });
      setCustomerResults(customerResponse.data || []);
    } catch (error) {
      toast.error('Failed to fetch required data');
    }
  };

  const fetchExistingVisit = async () => {
    try {
      const response = await siteVisitService.getById(id);
      const visit = response.data;

      const scheduled = visit.scheduledDate ? new Date(visit.scheduledDate) : null;
      const scheduledDate = scheduled
        ? scheduled.toISOString().slice(0, 10)
        : '';
      const scheduledTime = scheduled
        ? scheduled.toISOString().slice(11, 16)
        : '';

      setFormData((prev) => ({
        ...prev,
        complaint: visit.complaint || '',
        serviceType: visit.serviceType || 'Repair',
        priority: visit.priority || 'medium',
        assignedTechnician: visit.assignedTechnician?._id || '',
        assignedHelper: visit.assignedHelper?._id || '',
        scheduledDate,
        scheduledTime,
        notes: visit.notes || '',
      }));

      if (visit.customer && visit.customer._id) {
        setSelectedCustomerId(visit.customer._id);
        setSelectedCustomer(visit.customer);
        setCustomerResults((prev) => {
          const exists = prev.find((c) => c._id === visit.customer._id);
          if (exists) return prev;
          return [visit.customer, ...prev];
        });
      }

      setSelectedItems(
        (visit.itemsTaken || []).map((item) => ({
          inventory: item.inventory || item._id,
          itemName: item.itemName,
          quantity: item.quantity,
        }))
      );

      setSelectedTools(
        (visit.toolsTaken || []).map((tool) => ({
          tool: tool.tool || tool._id,
          toolName: tool.toolName,
        }))
      );
    } catch (error) {
      toast.error('Failed to load site visit for editing');
      navigate('/site-visits');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCustomerSearch = async () => {
    const term = customerSearch.trim();
    if (!term) {
      toast.error('Enter name, phone, or email to search customers');
      return;
    }
    setCustomerLoading(true);
    try {
      const response = await customerService.getAll({
        search: term,
        page: 1,
        limit: 10,
      });
      setCustomerResults(response.data || []);
      if (!response.data || response.data.length === 0) {
        toast.info('No customers found for this search');
      }
    } catch (error) {
      toast.error('Failed to search customers');
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomerId(customer._id);
    setSelectedCustomer(customer);
    toast.success('Customer details filled from selection');
  };

  const handleClearSelectedCustomer = () => {
    setSelectedCustomerId(null);
    setSelectedCustomer(null);
  };

  const handleCustomerSelectChange = (e) => {
    const id = e.target.value;
    if (!id) {
      setSelectedCustomerId(null);
      return;
    }
    const customer = customerResults.find((c) => c._id === id);
    if (customer) {
      handleSelectCustomer(customer);
    }
  };

  const handleAddItem = () => {
    if (!itemToAdd.inventory) {
      toast.error('Please select an item');
      return;
    }

    const item = inventoryItems.find((i) => i._id === itemToAdd.inventory);
    if (!item) return;

    if (item.quantity < itemToAdd.quantity) {
      toast.error('Insufficient stock quantity');
      return;
    }

    const alreadyAdded = selectedItems.find((i) => i.inventory === itemToAdd.inventory);
    if (alreadyAdded) {
      toast.error('Item already added');
      return;
    }

    setSelectedItems([
      ...selectedItems,
      {
        inventory: item._id,
        itemName: item.itemName,
        quantity: itemToAdd.quantity,
      },
    ]);

    setItemToAdd({ inventory: '', quantity: 1 });
  };

  const handleRemoveItem = (index) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  const handleAddTool = () => {
    if (!toolToAdd) {
      toast.error('Please select a tool');
      return;
    }

    const tool = tools.find((t) => t._id === toolToAdd);
    if (!tool) return;

    const alreadyAdded = selectedTools.find((t) => t.tool === toolToAdd);
    if (alreadyAdded) {
      toast.error('Tool already added');
      return;
    }

    setSelectedTools([
      ...selectedTools,
      {
        tool: tool._id,
        toolName: tool.toolName,
      },
    ]);

    setToolToAdd('');
  };

  const handleRemoveTool = (index) => {
    setSelectedTools(selectedTools.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate required fields
    if (!selectedCustomerId) {
      toast.error('Please select a customer');
      setLoading(false);
      return;
    }

    if (!formData.assignedTechnician) {
      toast.error('Please assign a technician');
      setLoading(false);
      return;
    }

    const scheduledDateTime = `${formData.scheduledDate}T${formData.scheduledTime}:00.000Z`;

    const submitData = {
      customer: selectedCustomerId,
      complaint: formData.complaint,
      serviceType: formData.serviceType,
      priority: formData.priority,
      assignedTechnician: formData.assignedTechnician,
      assignedHelper: formData.assignedHelper || undefined,
      scheduledDate: scheduledDateTime,
      itemsTaken: selectedItems.length > 0 ? selectedItems : undefined,
      toolsTaken: selectedTools.length > 0 ? selectedTools : undefined,
      notes: formData.notes || undefined,
    };

    // Note: ticketNumber is NOT included - it's auto-generated by the backend

    try {
      if (isEdit) {
        await siteVisitService.update(id, submitData);
        toast.success('Site visit updated successfully');
      } else {
        const response = await siteVisitService.create(submitData);
        toast.success(
          `Site visit created! Ticket: ${
            response.data?.ticketNumber || response.ticketNumber || 'Created'
          }`
        );
      }
      navigate('/site-visits');
    } catch (error) {
      console.error('Site visit save error:', error.response?.data);
      const errorMessage =
        error.response?.data?.message ||
        (isEdit ? 'Failed to update site visit' : 'Failed to create site visit');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate('/site-visits')} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">
          {isEdit ? 'Edit Site Visit' : 'Create Site Visit'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Customer Information</h3>
          <div className="customer-picker">
            <div className="customer-picker-header">
              <span className="customer-picker-title">Select Existing Customer</span>
              {selectedCustomerId && (
                <button
                  type="button"
                  className="customer-picker-clear"
                  onClick={handleClearSelectedCustomer}
                >
                  Clear selection
                </button>
              )}
            </div>
            <div className="customer-picker-controls">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search by name, phone, or email..."
                className="customer-picker-input"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleCustomerSearch}
                loading={customerLoading}
              >
                Search
              </Button>
            </div>
            <div className="customer-picker-select-wrap">
              <select
                className="input-field"
                value={selectedCustomerId || ''}
                onChange={handleCustomerSelectChange}
              >
                <option value="">Select customer from list</option>
                {customerResults.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.name} - {customer.phone}
                    {customer.email ? ` (${customer.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
            {selectedCustomer && (
              <div className="customer-picker-summary">
                <div className="customer-picker-summary-name">{selectedCustomer.name}</div>
                <div className="customer-picker-summary-line">
                  <span>{selectedCustomer.phone}</span>
                  {selectedCustomer.email && <span>{selectedCustomer.email}</span>}
                </div>
                {selectedCustomer.address && (
                  <div className="customer-picker-summary-line">
                    {[
                      selectedCustomer.address.street,
                      selectedCustomer.address.city,
                      selectedCustomer.address.state,
                      selectedCustomer.address.zipCode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Service Details</h3>
          <div className="form-grid">
            <div className="input-group full-width">
              <label className="input-label">
                Complaint Description <span className="required-asterisk">*</span>
              </label>
              <textarea
                name="complaint"
                value={formData.complaint}
                onChange={handleChange}
                placeholder="Describe the issue..."
                className="input-field"
                rows="3"
                required
              />
            </div>

            <div className="input-group">
              <label className="input-label">
                Service Type <span className="required-asterisk">*</span>
              </label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                className="input-field"
                required
              >
                <option value="Installation">Installation</option>
                <option value="Repair">Repair</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Inspection">Inspection</option>
                <option value="Emergency">Emergency</option>
              </select>
            </div>

            <div className="input-group">
              <label className="input-label">
                Priority <span className="required-asterisk">*</span>
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="input-field"
                required
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Assignment</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">
                Assign Technician <span className="required-asterisk">*</span>
              </label>
              <select
                name="assignedTechnician"
                value={formData.assignedTechnician}
                onChange={handleChange}
                className="input-field"
                required
              >
                <option value="">Select Technician</option>
                {technicians.map((tech) => {
                  const statusText = (tech.status || 'available').replace('_', ' ').toUpperCase();
                  return (
                    <option key={tech._id} value={tech._id}>
                      {tech.name} ({tech.employeeId || 'ID'}) — [{statusText}]
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="input-group">
              <label className="input-label">Assign Helper (Optional)</label>
              <select
                name="assignedHelper"
                value={formData.assignedHelper}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">No Helper</option>
                {helpers.map((helper) => (
                  <option key={helper._id} value={helper._id}>
                    {helper.name} ({helper.employeeId})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Schedule</h3>
          <div className="form-grid">
            <Input
              label="Date"
              type="date"
              name="scheduledDate"
              value={formData.scheduledDate}
              onChange={handleChange}
              required
            />

            <Input
              label="Time"
              type="time"
              name="scheduledTime"
              value={formData.scheduledTime}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Items to Take</h3>
          <div className="add-item-section">
            <select
              value={itemToAdd.inventory}
              onChange={(e) => setItemToAdd({ ...itemToAdd, inventory: e.target.value })}
              className="input-field"
            >
              <option value="">Select Item</option>
              {inventoryItems.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.itemName} (Available: {item.quantity})
                </option>
              ))}
            </select>

            <Input
              label=""
              type="number"
              value={itemToAdd.quantity}
              onChange={(e) => setItemToAdd({ ...itemToAdd, quantity: Number(e.target.value) })}
              min="1"
              placeholder="Qty"
            />

            <Button type="button" variant="secondary" onClick={handleAddItem}>
              Add Item
            </Button>
          </div>

          {selectedItems.length > 0 && (
            <div className="selected-items-list">
              {selectedItems.map((item, index) => (
                <div key={index} className="selected-item">
                  <span>
                    {item.itemName} - Qty: {item.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(index)}
                    className="remove-btn"
                  >
                    <FiX />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-section">
          <h3 className="section-title">Tools to Take</h3>
          <div className="add-item-section">
            <select
              value={toolToAdd}
              onChange={(e) => setToolToAdd(e.target.value)}
              className="input-field"
            >
              <option value="">Select Tool</option>
              {tools.map((tool) => (
                <option key={tool._id} value={tool._id}>
                  {tool.toolName} ({tool.toolCode})
                </option>
              ))}
            </select>

            <Button type="button" variant="secondary" onClick={handleAddTool}>
              Add Tool
            </Button>
          </div>

          {selectedTools.length > 0 && (
            <div className="selected-items-list">
              {selectedTools.map((tool, index) => (
                <div key={index} className="selected-item">
                  <span>{tool.toolName}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTool(index)}
                    className="remove-btn"
                  >
                    <FiX />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-section">
          <h3 className="section-title">Additional Notes</h3>
          <div className="input-group">
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any special instructions or notes..."
              className="input-field"
              rows="3"
            />
          </div>
        </div>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={() => navigate('/site-visits')}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" icon={<FiSave />} loading={loading}>
            {isEdit ? 'Update Site Visit' : 'Create Site Visit'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SiteVisitForm;
